import PropTypes from 'prop-types';
import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import superagent from 'superagent';

import {
  useAuth,
  useBrowser,
  useDisplay,
  useTownAssets,
} from '../stores';
import usePush from '../stores/push/usePush';
import {
  config,
  logError,
} from '../utils';

import { Button } from './buttons';
import {
  Column,
  Row,
} from './display';
import FullPopup from './display/FullPopup';
import Checkbox from './input/Checkbox';
import SelectDropdown from './input/SelectDropdown';
import TextInput from './input/TextInput';

const messageMaxLength = 20000;
const emailRegex = /^[^\s@\\]+@[^\s@.\\]+(?:\.[^\s@.\\]+)+$/;

const looksLikeEmail = (value) => (!!value) && emailRegex.test(value);

const buildDebug = ({
  auth,
  display,
  browser,
  wsConnected,
}) => {
  const win = globalThis;
  const nav = win.navigator ?? {};
  const url = (win.location)
    ? `${win.location.pathname}${win.location.search}${win.location.hash}`
    : undefined;

  const debug = {
    swVersion: String(config.rev),
    platform: 'www-town',
    os: browser?.osName,
    browser: browser?.browserName,
    browserVersion: browser?.browserVersion,
    darkMode: !!win.document?.documentElement?.classList?.contains('dark'),
    online: nav.onLine,
    wsConnected: !!wsConnected,
    viewport: (display?.browserWidthRaw && display?.browserHeightRaw)
      ? `${Math.round(display.browserWidthRaw)}x${Math.round(display.browserHeightRaw)}`
      : undefined,
    zoom: display?.zoom,
    locale: nav.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    url,
    cuid: auth?.info?.cuid,
    gpid: auth?.info?.grpGpid,
    rlid: auth?.info?.rlid,
    username: auth?.info?.username,
    fname: auth?.info?.fname,
    lname: auth?.info?.lname,
  };

  Object.keys(debug).forEach((k) => {
    if ((debug[k] === undefined) || (debug[k] === null) || (debug[k] === '')) {
      delete debug[k];
    }
  });

  return debug;
};

const SupportForm = function SupportForm({ onClose, supportCase = null }) {
  const { auth } = useAuth(false);
  const { display } = useDisplay(false);
  const { browser } = useBrowser(false);
  const { push } = usePush(false);
  const { assets: { translate } } = useTownAssets();

  const wsConnected = push?.wsSocketOpen;

  const presetMail = useMemo(
    () => (looksLikeEmail(auth?.info?.username) ? auth.info.username : ''),
    [auth],
  );

  const [reason, setReason] = useState('issue');
  const [message, setMessage] = useState('');
  const [mail, setMail] = useState(presetMail);
  const [includeMail, setIncludeMail] = useState(!!presetMail);
  const [phone, setPhone] = useState('');
  const [includePhone, setIncludePhone] = useState(false);
  const [includeDebug, setIncludeDebug] = useState(true);
  const [includeCase, setIncludeCase] = useState(false);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(null);

  // The server patterns, so a contact the server would refuse never leaves the form
  const serverRegex = auth?.server?.regex;
  const mailValid = useMemo(
    () => (!includeMail) || (!mail.trim()) || (serverRegex?.mail ?? []).every((rule) => rule.test(mail.trim())),
    [includeMail, mail, serverRegex],
  );
  const phoneValid = useMemo(
    () => (!includePhone) || (!phone.trim()) || (!serverRegex?.phone) || serverRegex.phone.test(phone.trim()),
    [includePhone, phone, serverRegex],
  );

  const reasonChoices = useMemo(
    () => [
      { value: 'issue', text: translate('issue') ?? 'issue' },
      { value: 'suggestion', text: translate('suggestion') ?? 'suggestion' },
      { value: 'question', text: translate('question') ?? 'question' },
      { value: 'other', text: translate('other') ?? 'other' },
    ],
    [translate],
  );

  const onReasonChange = useCallback(
    (event) => setReason(event.currentTarget.value),
    [],
  );
  const onMessageChange = useCallback(
    (event) => setMessage(event.currentTarget.value),
    [],
  );
  const onMailChange = useCallback(
    (event) => setMail(event.currentTarget.value),
    [],
  );
  const onPhoneChange = useCallback(
    (event) => setPhone(event.currentTarget.value),
    [],
  );
  const onIncludeMailChange = useCallback(
    (event) => setIncludeMail(!!event.currentTarget.value),
    [],
  );
  const onIncludePhoneChange = useCallback(
    (event) => setIncludePhone(!!event.currentTarget.value),
    [],
  );
  const onIncludeDebugChange = useCallback(
    (event) => setIncludeDebug(!!event.currentTarget.value),
    [],
  );
  const onIncludeCaseChange = useCallback(
    (event) => setIncludeCase(!!event.currentTarget.value),
    [],
  );

  const submit = useCallback(
    async () => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) {
        setSendError(translate('messageErr') ?? 'message required');
        return;
      }

      // A request about a case goes through the authenticated route, which
      // takes the requester from the session instead of honeypot fields
      const aboutCase = includeCase && supportCase;
      const body = {
        reason,
        message: trimmedMessage,
        ...(aboutCase ? { csid: supportCase.csid } : { hp_email: '', hp_name: '' }),
      };
      if (includeMail && mail.trim()) body.mail = mail.trim();
      if (includePhone && phone.trim()) body.phone = phone.trim();
      if (includeDebug) {
        body.debug = buildDebug({
          auth, display, browser, wsConnected,
        });
      }

      setSending(true);
      setSendError(null);

      try {
        const url = `${config.town.back.urlPrefix}support/${aboutCase ? 'sendCase' : 'send'}`;
        const res = (auth?.superagentPostAsync)
          ? await auth.superagentPostAsync(
              url,
              ['Content-Type', 'application/json'],
              body,
            )
          : await superagent
              .post(url)
              .set('Content-Type', 'application/json')
              .send(body);
        const data = res.body?.rslt?.data;
        if (data?.success) {
          setSent(true);
        } else if (data?.error === 'rateLimited') {
          setSendError(translate('rateLimited') ?? 'too many requests, try again later');
        } else {
          setSendError(translate('sendFailure') ?? 'send failed');
        }
      } catch (err) {
        logError(
          `SupportForm submit ${err}`, // NOSONAR error stringified for logging
          'SupportForm.SupportForm.error',
          {
            err: err?.message ?? String(err), // NOSONAR error stringified for logging
            ...(err?.status && { status: err.status }),
          },
        );
        setSendError(translate('sendFailure') ?? 'send failed');
      } finally {
        setSending(false);
      }
    },
    [
      reason, message, mail, phone,
      includeMail, includePhone, includeDebug, includeCase, supportCase,
      auth, display, browser, wsConnected,
      translate,
    ],
  );

  return (
    <FullPopup
      onClose={onClose}
      small
      pending={sending}
    >
      <Column gap={3} className="text-pxTxt w-[min(90vw,520px)] py-2 text-wrap">
        {(sent)
          ? (
              <div className="text-center py-6 text-pxUiOk text-lg font-semibold">
                {translate('messageSent')}
              </div>
            )
          : (
              <>
                <span className="self-start">
                  {`${translate('formIntro', 'support')}\u00A0:`}
                </span>
                <TextInput
                  id="message"
                  placeholder={translate('message')}
                  value={message}
                  onChange={onMessageChange}
                  multiline
                  fullWidth
                  invalid={message.length > messageMaxLength}
                />

                <span className="self-start">
                  {translate('formReason', 'support')}
                </span>
                <SelectDropdown
                  id="reason"
                  label={translate('reason') ?? 'reason'}
                  value={reason}
                  choices={reasonChoices}
                  onChange={onReasonChange}
                />

                <span className="self-start">
                  {`${translate('contactInfo', 'support')}\u00A0:`}
                </span>
                <Row gap={2}>
                  <Checkbox
                    id="includeMail"
                    checked={includeMail}
                    onChange={onIncludeMailChange}
                  />
                  <TextInput
                    id="mail"
                    placeholder={translate('email')}
                    value={mail}
                    onChange={onMailChange}
                    fillType="email"
                    autoFill
                    fullWidth
                    disabled={!includeMail}
                    invalid={!mailValid}
                  />
                </Row>

                <Row gap={2}>
                  <Checkbox
                    id="includePhone"
                    checked={includePhone}
                    onChange={onIncludePhoneChange}
                  />
                  <TextInput
                    id="phone"
                    placeholder={translate('phone')}
                    value={phone}
                    onChange={onPhoneChange}
                    fillType="tel"
                    autoFill
                    fullWidth
                    disabled={!includePhone}
                    invalid={!phoneValid}
                  />
                </Row>

                {(supportCase)
                  ? (
                      <Row gap={2}>
                        <Checkbox
                          id="includeCase"
                          checked={includeCase}
                          onChange={onIncludeCaseChange}
                        />
                        <span className="text-sm truncate">
                          {`${translate('aboutCaseNbr', 'support', { nbr: supportCase.csUid })} - ${supportCase.label}`}
                        </span>
                      </Row>
                    )
                  : null}

                <Row gap={2}>
                  <Checkbox
                    id="includeDebug"
                    checked={includeDebug}
                    onChange={onIncludeDebugChange}
                  />
                  <span className="text-sm">
                    {translate('includeDebug', 'support')}
                  </span>
                </Row>

                {(sendError)
                  ? <div className="text-pxUiAlert text-sm text-center">{sendError}</div>
                  : null}

                <Button
                  id="submit"
                  label={translate('send')}
                  onClick={submit}
                  disabled={sending || (!message.trim()) || (!mailValid) || (!phoneValid)}
                  color="primary"
                  baseColor
                />
              </>
            )}
      </Column>
    </FullPopup>
  );
};

SupportForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  // The case open on screen, offered as the subject of the request (admins only)
  supportCase: PropTypes.shape({
    csid: PropTypes.string.isRequired,
    csUid: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
  }),
};

export default SupportForm;
